import {
  MedicalRecordForm,
  MedicalRecordPayload,
  MedicationStatus,
  PatientGoalType,
} from 'models/MedicalRecord'
import { MedicalRecordFormData } from './medicalRecordSchema'

import { OTHER_MEDICATION_OPTION_ID } from './types/option'

const OTHERS_LABEL = 'Outros(as)'

const patientGoalMap = {
  'patient-goal-action': PatientGoalType.ACTION,
  'patient-goal-pre-contemplation': PatientGoalType.PRE_CONTEMPLATION,
  'patient-goal-contemplation': PatientGoalType.CONTEMPLATION,
  'patient-goal-planning-or-preparation':
    PatientGoalType.PLANNING_OR_PREPARATION,
  'patient-goal-maintenance-or-relapse': PatientGoalType.MAINTENANCE_OR_RELAPSE,
}

const patientGoalResponseMap = {
  [PatientGoalType.ACTION]: 'patient-goal-action',
  [PatientGoalType.PRE_CONTEMPLATION]: 'patient-goal-pre-contemplation',
  [PatientGoalType.CONTEMPLATION]: 'patient-goal-contemplation',
  [PatientGoalType.PLANNING_OR_PREPARATION]: 'patient-goal-planning-or-preparation',
  [PatientGoalType.MAINTENANCE_OR_RELAPSE]: 'patient-goal-maintenance-or-relapse',
}

export const medicalRecordFormatData = (
  formData: MedicalRecordFormData,
  currentForm: MedicalRecordForm,
): MedicalRecordPayload => {
  const {
    complaints,
    symptoms,
    medications,
    diagnostic_hypotheses,
    patient_needs_to_continue_treatment,
    other_complaint,
    other_symptom,
    other_patient_consumption,
    other_medication,
    patient_consumptions,
    symptoms_impact_on_work_life,
    has_suicidal_ideation,
    has_psychiatrist_treatment,
    patient_goal,
    patient_case_complexity,
    last_patient_case_complexity,
    patient_case_description,
    medical_professional_referral,
    type,
    files,
  } = formData

  const filteredMedications = medications?.filter(medication => medication.id !== OTHER_MEDICATION_OPTION_ID)

  const otherMedication = currentForm?.other_medications.find(otherMedication =>
    otherMedication.id === medications?.find((medication) => medication?.id === OTHER_MEDICATION_OPTION_ID)?.medication_medical_record_form_id)

  const formatOtherMedicationStatus = (): MedicationStatus => {
    let otherMedicationStatus
    medications.forEach((medication) => {
      if (medication.id === OTHER_MEDICATION_OPTION_ID) {
        otherMedicationStatus = medication.status?.id as MedicationStatus
      }
    })
    return otherMedicationStatus
  }

  function getLastWordAfterHyphen(id: string): string {
    const parts = id.split('-')
    return parts[parts.length - 1] || ''
  }

  function patientConsumptionsWithoutOthers() {
    return patient_consumptions.filter(
      (patientConsumption) => !patientConsumption.includes('others')
    )
  }

  return {
    complaints: complaints?.map((data) => ({
      id: data.id,
      ciap_code: data.ciapCode,
      description: data.description,
    })),
    symptoms: symptoms?.map((data) => ({
      id: data.id,
      description: data.description,
    })),
    medications: filteredMedications?.map((medication) => ({
      id: medication.id,
      status: medication.status?.id,
      generic_name: medication?.description,
      commercial_name: medication?.commercialName,
      medication_medical_record_form_id: medication.medication_medical_record_form_id,
    })),
    diagnostic_hypotheses: diagnostic_hypotheses?.map((diagnostic) => ({
      id: diagnostic.id,
      status: diagnostic.status?.id,
      cid_code: diagnostic?.cidCode,
      description: diagnostic?.description,
      diagnostic_hypothesis_medical_record_form_id: diagnostic.diagnostic_hypothesis_medical_record_form_id
    })),
    patient_needs_to_continue_treatment: patient_needs_to_continue_treatment?.need_to_continue_treatment && {
      number_of_schedules:
        patient_needs_to_continue_treatment.number_of_schedules,
      reason: patient_needs_to_continue_treatment.reason,
      other_reason: patient_needs_to_continue_treatment.other_reason,
      need_to_continue_treatment: patient_needs_to_continue_treatment.need_to_continue_treatment?.includes(
        'yes'
      ),
    },
    other_complaint: other_complaint && { description: other_complaint },
    other_symptom: other_symptom && { description: other_symptom },
    other_patient_consumption: other_patient_consumption && {
      description: other_patient_consumption,
    },
    other_medications: other_medication ? [{
      id: otherMedication?.id ?? undefined,
      description: other_medication,
      status: formatOtherMedicationStatus(),
    }] : undefined,
    patient_consumptions:
      patient_consumptions &&
      patientConsumptionsWithoutOthers().map((patientConsumption) =>
        getLastWordAfterHyphen(patientConsumption).toUpperCase()
      ),
    symptoms_impact_on_work_life:
      symptoms_impact_on_work_life &&
      getLastWordAfterHyphen(symptoms_impact_on_work_life).toUpperCase(),
    has_suicidal_ideation: has_suicidal_ideation?.includes('yes'),
    has_psychiatrist_treatment: has_psychiatrist_treatment?.includes('yes'),
    patient_goal: patientGoalMap[patient_goal],
    last_patient_case_complexity,
    patient_case_complexity:
      patient_case_complexity &&
      getLastWordAfterHyphen(patient_case_complexity).toUpperCase(),
    patient_case_description,
    referral_to_health_care_professional: medical_professional_referral,
    type,
    files: files?.length > 0 ? files : undefined,
  } as MedicalRecordPayload
}

const diagnosticHypothesesStatusOptions = [
  { id: 'REFERRED', label: 'Referido' },
  { id: 'HYPOTHESIS', label: 'Hipótese' },
  { id: 'CONFIRMED', label: 'Confirmado' },
  { id: 'DONE', label: 'Encerrado' },
]

const medicationStatusOptions = [
  { id: 'IN_USE', label: 'Em uso' },
  { id: 'INTERRUPTED', label: 'Interrompido' },
]

export const medicalRecordFormFormatResponse = (
  formData: MedicalRecordForm
): MedicalRecordFormData => {
  const {
    medications,
    diagnostic_hypotheses,
    other_medications,
    other_complaint,
    other_symptom,
    patient_case_description,
    patient_goal,
    other_patient_consumption,
    patient_case_complexity,
    referral_to_healthcare_professional,
    symptoms_impact_on_work_life,
    type,
    complaints,
    symptoms,
    patient_needs_to_continue_treatment,
    patient_consumptions,
    has_suicidal_ideation,
    last_patient_case_complexity,
    has_psychiatrist_treatment,
    files,
  } = formData

  const formatMedications = () => {
    if (other_medications.length > 0) {
      const firstOtherMedication = other_medications[0]
      medications.push({
        medication_medical_record_form_id: other_medications[0].id,
        medication: {
          id: OTHER_MEDICATION_OPTION_ID,
          commercial_name: OTHERS_LABEL,
          generic_name: OTHERS_LABEL,
        },
        status: firstOtherMedication.status as MedicationStatus
      })
    }
    return medications.map(med => ({
      id: med.medication.id,
      commercialName: med.medication.commercial_name,
      description: med.medication.generic_name,
      name: med.medication.generic_name,
      medication_medical_record_form_id: med.medication_medical_record_form_id ?? undefined,
      status: medicationStatusOptions.find(status => status?.id.includes(med.status))
    }))
  }

  const formatDiagnosticHypotheses = () =>
    diagnostic_hypotheses.map(diagnostic => ({
      id: diagnostic.diagnostic_hypothesis?.id,
      cidCode: diagnostic.diagnostic_hypothesis?.cid_code,
      description: diagnostic.diagnostic_hypothesis?.description,
      diagnostic_hypothesis_medical_record_form_id: diagnostic.diagnostic_hypothesis_medical_record_form_id ?? undefined,
      name: `${diagnostic.diagnostic_hypothesis?.description} (CID ${diagnostic.diagnostic_hypothesis?.cid_code})`,
      status: diagnosticHypothesesStatusOptions.find(status => status?.id.includes(diagnostic.status))
    }))

  const formatComplaints = () =>
    complaints.map(complaint => ({
      id: complaint.complaint.id,
      ciapCode: complaint.complaint.ciap_code,
      description: complaint.complaint.description,
      name: complaint.complaint.description,
    }))

  const formatSymptoms = () =>
    symptoms.map(symptom => ({
      id: symptom.symptom.id,
      description: symptom.symptom.description,
      name: symptom.symptom.description,
    }))

  const symptomsImpact = symptoms_impact_on_work_life ?
    `other-symptoms-impact-on-work-life-${symptoms_impact_on_work_life?.toLowerCase()}`
    : undefined

  if (other_patient_consumption.description) {
    patient_consumptions.push('others')
  }

  const need_to_continue_treatment = patient_needs_to_continue_treatment.need_to_continue_treatment

  return {
    type: type,
    medications: formatMedications(),
    diagnostic_hypotheses: formatDiagnosticHypotheses(),
    other_medication: other_medications[0]?.commercial_name || '',
    other_complaint: other_complaint.description,
    other_symptom: other_symptom.description,
    patient_case_description,
    patient_goal: patientGoalResponseMap[patient_goal],
    other_patient_consumption: other_patient_consumption.description,
    patient_case_complexity: patient_case_complexity ? `patient-case-complexity-${patient_case_complexity?.toLowerCase()}` : undefined,
    medical_professional_referral: referral_to_healthcare_professional,
    symptoms_impact_on_work_life: symptomsImpact,
    complaints: formatComplaints(),
    symptoms: formatSymptoms(),
    patient_needs_to_continue_treatment: {
      need_to_continue_treatment: need_to_continue_treatment != undefined ? `patient-needs-to-continue-treatment-${need_to_continue_treatment ? 'yes' : 'no'}` : undefined,
      number_of_schedules: patient_needs_to_continue_treatment.number_of_schedules ?? undefined  ,
      other_reason: patient_needs_to_continue_treatment.other_reason ?? undefined,
      reason: patient_needs_to_continue_treatment.reason ?? undefined ,
    },
    patient_consumptions: patient_consumptions.map(consumption => `patient-consumption-${consumption?.toLowerCase()}`),
    has_psychiatrist_treatment: has_psychiatrist_treatment != undefined ? `psychiatrist-treatment-${has_psychiatrist_treatment ? 'yes' : 'no'}` : undefined,
    has_suicidal_ideation: has_suicidal_ideation != undefined ? `suicidal-ideation-${has_suicidal_ideation ? 'yes' : 'no'}` : undefined,
    last_patient_case_complexity: last_patient_case_complexity,
    files,
  } as MedicalRecordFormData
}
